export const INSTALL_PROMPT_STATUS_KEY = 'cq_install_prompt_status';
export const INSTALL_PROMPT_DISMISSED_AT_KEY = 'cq_install_prompt_dismissed_at';
export const INSTALL_PROMPT_LAST_SHOWN_AT_KEY = 'cq_install_prompt_last_shown_at';

const DISMISS_SUPPRESSION_DAYS = 30;
const MIN_DAYS_BETWEEN_PROMPTS = 1;
const STATUS_NEVER = 'never';
const STATUS_DISMISSED = 'dismissed';
const STATUS_INSTALLED = 'installed';

const hasBrowserStorage = () => (
  typeof window !== 'undefined' &&
  typeof window.localStorage !== 'undefined'
);

const getNavigator = () => (
  typeof window !== 'undefined' ? window.navigator : undefined
);

function daysSince(timestamp) {
  if (!timestamp) {
    return Infinity;
  }

  const time = Number(timestamp);

  if (!Number.isFinite(time)) {
    return Infinity;
  }

  return (Date.now() - time) / (1000 * 60 * 60 * 24);
}

export function isRunningStandalone() {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator?.standalone === true
  );
}

export function isIOS() {
  const navigatorRef = getNavigator();

  if (!navigatorRef) {
    return false;
  }

  const ua = navigatorRef.userAgent || '';
  const platform = navigatorRef.platform || '';

  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (platform === 'MacIntel' && navigatorRef.maxTouchPoints > 1)
  );
}

export function isSafari() {
  const navigatorRef = getNavigator();
  const ua = navigatorRef?.userAgent || '';

  return /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
}

export function isIOSSafari() {
  return isIOS() && isSafari();
}

export function isAndroid() {
  const navigatorRef = getNavigator();
  const ua = navigatorRef?.userAgent || '';

  return /Android/i.test(ua);
}

export function getInstallPromptStatus() {
  if (!hasBrowserStorage()) {
    return STATUS_NEVER;
  }

  return window.localStorage.getItem(INSTALL_PROMPT_STATUS_KEY) || STATUS_NEVER;
}

export function markInstallPromptShown() {
  if (!hasBrowserStorage()) {
    return;
  }

  window.localStorage.setItem(INSTALL_PROMPT_LAST_SHOWN_AT_KEY, String(Date.now()));
}

export function markInstallPromptDismissed() {
  if (!hasBrowserStorage()) {
    return;
  }

  window.localStorage.setItem(INSTALL_PROMPT_STATUS_KEY, STATUS_DISMISSED);
  window.localStorage.setItem(INSTALL_PROMPT_DISMISSED_AT_KEY, String(Date.now()));
}

export function markInstallPromptInstalled() {
  if (!hasBrowserStorage()) {
    return;
  }

  window.localStorage.setItem(INSTALL_PROMPT_STATUS_KEY, STATUS_INSTALLED);
}

export function shouldShowInstallPrompt() {
  if (!hasBrowserStorage() || isRunningStandalone()) {
    return false;
  }

  const status = getInstallPromptStatus();

  if (status === STATUS_INSTALLED) {
    return false;
  }

  const dismissedAt = window.localStorage.getItem(INSTALL_PROMPT_DISMISSED_AT_KEY);
  const lastShownAt = window.localStorage.getItem(INSTALL_PROMPT_LAST_SHOWN_AT_KEY);

  if (
    status === STATUS_DISMISSED &&
    daysSince(dismissedAt) < DISMISS_SUPPRESSION_DAYS
  ) {
    return false;
  }

  if (daysSince(lastShownAt) < MIN_DAYS_BETWEEN_PROMPTS) {
    return false;
  }

  return true;
}

export function getInstallPromptVariant(canUseNativePrompt) {
  if (canUseNativePrompt) {
    return 'native';
  }

  if (isIOSSafari()) {
    return 'ios';
  }

  if (isAndroid()) {
    return 'android';
  }

  return null;
}
